import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, ArrowRight } from 'lucide-react';
import { useDocuments } from '../../hooks/useDocuments';
import { ROUTES } from '../../lib/constants';
import { formatRelativeTime } from '../../utils/formatters';

// Clean inline row skeletons matching real entry items
const RecentDocumentSkeleton = () => (
  <div className="flex items-center space-x-4 p-4 rounded-xl bg-white/20 animate-pulse border border-transparent">
    <div className="w-10 h-10 bg-brown-200 rounded-lg" />
    <div className="flex-1 space-y-2">
      <div className="h-4 bg-brown-300/40 rounded w-1/3" />
      <div className="h-3 bg-brown-200/40 rounded w-1/4" />
    </div>
  </div>
);

const RecentDocuments = ({ limit = 5 }) => {
  const navigate = useNavigate();
  const { documents, loading } = useDocuments(limit);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100/60 text-green-700 border-green-200/50';
      case 'processing':
        return 'bg-blue-100/60 text-blue-700 border-blue-200/50';
      case 'pending':
        return 'bg-yellow-100/60 text-yellow-700 border-yellow-200/50';
      case 'failed':
        return 'bg-red-100/60 text-red-700 border-red-200/50';
      default:
        return 'bg-gray-100/60 text-gray-700 border-gray-200/50';
    }
  };

  return (
    <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-5 gap-2 sm:gap-0">
        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900">
          Recent Documents
        </h3>
        {!loading && documents.length > 0 && (
          <button
            onClick={() => navigate(ROUTES.HISTORY)}
            className="text-xs sm:text-sm text-brown-700 hover:text-brown-900 font-medium flex items-center space-x-1 self-start sm:self-auto"
          >
            <span>View all</span>
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        )}
      </div>

      <div className="space-y-2 sm:space-y-3">
        {loading ? (
          <>
            <RecentDocumentSkeleton />
            <RecentDocumentSkeleton />
            <RecentDocumentSkeleton />
          </>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 sm:py-10">
            <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-cream-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-sm sm:text-base text-brown-600 mb-4 sm:mb-5">
              No documents yet
            </p>
            <button
              onClick={() => navigate(ROUTES.UPLOAD)}
              className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-brown-600 to-brown-700 text-white rounded-lg hover:from-brown-700 hover:to-brown-800 transition-all text-sm sm:text-base font-medium shadow-md"
            >
              Upload Your First Document
            </button>
          </div>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.id}
              onClick={() => navigate(ROUTES.DOCUMENT.replace(':id', doc.id))}
              className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-lg sm:rounded-xl hover:bg-cream-50/50 transition-all cursor-pointer group border border-transparent hover:border-cream-200/50"
            >
              <div className="flex-shrink-0 mt-1">
                <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 bg-gradient-to-br from-brown-100 to-brown-200 rounded-lg flex items-center justify-center group-hover:from-brown-200 group-hover:to-brown-300 transition-all">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-brown-700" />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm md:text-base font-medium text-brown-900 truncate group-hover:text-brown-700 transition-colors">
                  {doc.file_name}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1 sm:mt-1.5">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(doc.processing_status)}`}>
                    {doc.processing_status}
                  </span>
                  <span className="text-xs text-brown-500 flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatRelativeTime(doc.created_at)}
                  </span>
                </div>
              </div>

              <div className="flex-shrink-0 hidden sm:block">
                <ArrowRight className="w-5 h-5 text-brown-400 group-hover:text-brown-700 transition-colors" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentDocuments;